import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { APPROVAL_LABELS, APPROVAL_COLORS } from '../staff/RepairApprovalActions';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const RepairRequests = ({ propertyId }) => {
  const [repairs, setRepairs] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleRepairs, resultCount, totalCount
  } = useTableSearch(repairs, {
    searchFields: ['title', 'description'],
    filters: {
      status: (row, v) => row.status === v
    }
  });
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    priority: 'medium',
  });

  useEffect(() => {
    fetchRepairs();
    fetchCategories();
  }, []);

  const fetchRepairs = async () => {
    try {
      const response = await axios.get('/repairs');
      setRepairs(response.data);
    } catch (error) {
      toast.error('Error fetching repair requests');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/repair-categories?isActive=true');
      setCategories(response.data || []);
    } catch (error) {
      // Non-fatal: the category picker just stays empty and the request is still
      // submittable, since category is optional.
      setCategories([]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please provide a title and description');
      return;
    }

    setSubmitting(true);
    try {
      // The server derives property, unit, tenant and status from the session, so only
      // the descriptive fields are sent.
      await axios.post('/repairs', {
        title: formData.title,
        description: formData.description,
        categoryId: formData.categoryId || null,
        priority: formData.priority,
      });
      toast.success('Repair request submitted');
      setOpen(false);
      setFormData({ title: '', description: '', categoryId: '', priority: 'medium' });
      fetchRepairs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error submitting request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      medium: 'info',
      high: 'warning',
      urgent: 'error',
    };
    return colors[priority] || 'default';
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Repair Requests</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          New Request
        </Button>
      </Box>

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Title or description"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
          { key: 'status', label: 'Status', value: filterValues.status || '',
            onChange: (v) => setFilter('status', v),
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'in_progress', label: 'In progress' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' }
            ] }
        ]}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRepairs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No repair requests found
                </TableCell>
              </TableRow>
            ) : (
              visibleRepairs.map((repair) => (
                <TableRow key={repair._id}>
                  <TableCell>{repair.title}</TableCell>
                  <TableCell>{repair.category?.name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={APPROVAL_LABELS[repair.approvalStatus] || 'Draft'}
                      color={APPROVAL_COLORS[repair.approvalStatus] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={repair.priority}
                      color={getPriorityColor(repair.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={repair.status}
                      color={getStatusColor(repair.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {format(new Date(repair.createdAt), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>{repair.description}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Repair Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            margin="normal"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
          <TextField
            fullWidth
            select
            label="Category"
            margin="normal"
            value={formData.categoryId}
            onChange={(e) =>
              setFormData({ ...formData, categoryId: e.target.value })
            }
            helperText="Optional — helps us route your request to the right person"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {categories.map((c) => (
              <MenuItem key={c._id} value={c._id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label="Priority"
            margin="normal"
            value={formData.priority}
            onChange={(e) =>
              setFormData({ ...formData, priority: e.target.value })
            }
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.title.trim() || !formData.description.trim()}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RepairRequests;


